import { useState, useEffect, useRef } from 'react';
import { Button } from '@features/ui';
import { ExportIcon } from '@features/ui/icons';
import { PaTradersWithExcessiveDailyProfitTable } from '@features/admin/reports/PaTradersWithExcessiveDailyProfitTable.tsx';
import {
  getActiveLifetimePA,
  exportActiveLifetimePA,
} from '@src/services/reports';
import {
  TActiveLifetimePAData,
  TActiveLifetimePAParams,
} from '@src/types/reports';
import { downloadFile } from '@lib/utils';
import { toast } from 'react-toastify';

export const PaTradersWithExcessiveDailyProfit = () => {
  const [data, setData] = useState<TActiveLifetimePAData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState<TActiveLifetimePAParams>({
    not_traded_days: 12,
    in_period_days: 30,
    page: 1,
    page_size: 20,
  });
  const isInitialMount = useRef(true);

  const handleGetData = async () => {
    setIsLoading(true);
    try {
      const res = await getActiveLifetimePA(filters);

      setData(res.data);
      setTotalCount(res.total_count);
      setTotalPages(res.total_pages);
      toast.success('Data fetched successfully');
    } catch (error) {
      toast.error('Failed to fetch data. Please try again.');
      console.error('Error fetching Active Lifetime PA data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  // Auto-fetch when page changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;

      return;
    }
    handleGetData();
  }, [filters.page]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const res = await exportActiveLifetimePA(filters);

      downloadFile(res.data, 'active-lifetime-pa');
      toast.success('Export successful');
    } catch (error) {
      toast.error('Failed to export data. Please try again.');
      console.error('Error exporting Active Lifetime PA data:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div
        className={
          'flex justify-between lg:items-center gap-7.5 lg:flex-row flex-col'
        }
      >
        <div className={'text-xs flex lg:flex-row flex-col gap-x-8 gap-y-3'}>
          <h1
            className={'text-[23px]'}
          >{`Active Lifetime PA not Traded in 'X' Days`}</h1>
          <p className={'max-w-[482px]'}>
            {`Showing ${totalCount} Lifetime PA accounts that have NOT traded ${filters.not_traded_days} days in the last ${filters.in_period_days} calendar days.`}
          </p>
        </div>
        <Button
          className={'h-9.5 border-1 border-[#C6C6C6] rounded-[3px] w-fit'}
          disabled={isExporting}
          variant={'outline'}
          onClick={handleExport}
        >
          <p>{isExporting ? 'Exporting...' : 'Export'}</p>
          <ExportIcon />
        </Button>
      </div>
      <PaTradersWithExcessiveDailyProfitTable
        data={data}
        filters={filters}
        isLoading={isLoading}
        totalPages={totalPages}
        onFiltersChange={setFilters}
        onGetData={handleGetData}
        onPageChange={handlePageChange}
      />
    </div>
  );
};
